import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Paper,
  Link
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  Assessment as AssessmentIcon,
  AttachMoney as MoneyIcon,
  Code as CodeIcon,
  Cloud as CloudIcon,
  Security as SecurityIcon,
  Analytics as AnalyticsIcon,
  Public as PublicIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const ACEATAPBridgeContractDetail: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/admin/dev-technology-contracts');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to Dev Technology Contracts"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: '#003366', fontWeight: 'bold' }}>
            ACE and ATAP Platforms Bridge Contract
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            $43.8 Million - Interim Bridge Task Order
          </Typography>
        </Box>
      </Box>

      {/* Contract Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Contract Overview
          </Typography>
          <Typography variant="body1" paragraph>
            Dev Technology Group is the prime contractor for a $43.8 million interim bridge task order awarded in 
            October 2025, supporting the Automated Commercial Environment (ACE) and Advanced Trade Analytics Program 
            (ATAP). This critical bridge contract ensures continuous operations and development support for CBP's 
            Cargo Services Program Directorate while long-term modernization efforts are underway. The work involves 
            Agile development, DevSecOps, legacy modernization, data management, and expansion of analytics capabilities.
          </Typography>
        </CardContent>
      </Card>

      {/* Contract Details */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Contract Details
      </Typography>

      <Card sx={{ mb: 4 }}>
        <TableContainer>
          <Table aria-label="ACE and ATAP Bridge Contract Details">
            <TableBody>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ width: '25%', bgcolor: '#003366', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                >
                  Contract Value
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    <strong>$43,800,000</strong> (Forty-Three Million Eight Hundred Thousand Dollars)
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#1976D2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                >
                  Contract Type
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    <strong>Interim Bridge Task Order</strong>
                  </Typography>
                  <Typography variant="caption" display="block">
                    Ensures continuity during long-term modernization transition
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#2E7D32', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                >
                  Award Date
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    <strong>October 2025</strong>
                  </Typography>
                  <Typography variant="caption" display="block">
                    Recent award for immediate support
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#F57C00', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                >
                  Contracting Office
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    <strong>CBP Cargo Services Program Directorate</strong>
                  </Typography>
                  <Typography variant="caption" display="block">
                    U.S. Customs and Border Protection
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#7B1FA2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                >
                  Prime Contractor
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    <strong>Dev Technology Group</strong>
                  </Typography>
                  <Typography variant="caption" display="block">
                    Sole prime contractor
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#D32F2F', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                >
                  Status
                </TableCell>
                <TableCell>
                  <Chip label="Active" color="success" />
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Current operations supporting ACE and ATAP
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Bridge Contract Purpose */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Bridge Contract Purpose & Strategy
      </Typography>

      <Card sx={{ mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976D2' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
            Why a Bridge Contract?
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Continuity of Operations:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Ensures uninterrupted ACE and ATAP operations</Typography></li>
                <li><Typography variant="body2">Maintains 24/7 support for mission-critical systems</Typography></li>
                <li><Typography variant="body2">Prevents service gaps during modernization transition</Typography></li>
                <li><Typography variant="body2">Protects $2.5T+ annual U.S. trade processing</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Strategic Transition:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Bridges gap to long-term modernization contract</Typography></li>
                <li><Typography variant="body2">Allows time for comprehensive procurement process</Typography></li>
                <li><Typography variant="body2">Maintains development momentum and progress</Typography></li>
                <li><Typography variant="body2">Enables seamless transition to future contracts</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Scope of Work */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <CodeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Scope of Work & Services
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #003366' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CodeIcon sx={{ fontSize: 40, color: '#003366', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#003366', fontWeight: 'bold' }}>
                  Agile Development
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Sprint-based development for ACE and ATAP enhancements.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Scrum Teams:</strong> Agile development methodology</Typography></li>
                <li><Typography variant="body2"><strong>Sprint Delivery:</strong> 2-week sprint cycles</Typography></li>
                <li><Typography variant="body2"><strong>Feature Development:</strong> New capabilities and enhancements</Typography></li>
                <li><Typography variant="body2"><strong>Bug Fixes:</strong> Rapid issue resolution</Typography></li>
                <li><Typography variant="body2"><strong>User Stories:</strong> Business-driven development</Typography></li>
                <li><Typography variant="body2"><strong>Continuous Integration:</strong> Automated build and test</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  DevSecOps
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Integrated security and operations practices.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>CI/CD Pipelines:</strong> Automated deployment workflows</Typography></li>
                <li><Typography variant="body2"><strong>Security Scanning:</strong> Automated vulnerability detection</Typography></li>
                <li><Typography variant="body2"><strong>Code Quality:</strong> Static analysis and code reviews</Typography></li>
                <li><Typography variant="body2"><strong>Automated Testing:</strong> Unit, integration, performance tests</Typography></li>
                <li><Typography variant="body2"><strong>Infrastructure as Code:</strong> Terraform, CloudFormation</Typography></li>
                <li><Typography variant="body2"><strong>Monitoring:</strong> Application performance management</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CloudIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  Legacy Modernization
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Upgrading and modernizing existing systems.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Code Refactoring:</strong> Improving legacy codebase</Typography></li>
                <li><Typography variant="body2"><strong>Technology Upgrades:</strong> Framework and library updates</Typography></li>
                <li><Typography variant="body2"><strong>Architecture Improvements:</strong> Microservices migration</Typography></li>
                <li><Typography variant="body2"><strong>Cloud Migration:</strong> Moving to cloud infrastructure</Typography></li>
                <li><Typography variant="body2"><strong>Performance Optimization:</strong> System speed improvements</Typography></li>
                <li><Typography variant="body2"><strong>Technical Debt Reduction:</strong> Addressing legacy issues</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AnalyticsIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                  Data Management & Analytics
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Enhanced data capabilities and analytics expansion.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Data Pipeline Development:</strong> ETL processes and workflows</Typography></li>
                <li><Typography variant="body2"><strong>Analytics Expansion:</strong> New analytical capabilities</Typography></li>
                <li><Typography variant="body2"><strong>Dashboard Development:</strong> Visualization and reporting</Typography></li>
                <li><Typography variant="body2"><strong>Data Quality:</strong> Validation and cleansing</Typography></li>
                <li><Typography variant="body2"><strong>Data Governance:</strong> Policies and standards</Typography></li>
                <li><Typography variant="body2"><strong>Performance Tuning:</strong> Query optimization</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Systems Supported */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <PublicIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Systems Supported Under Bridge Contract
      </Typography>

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#003366' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>System</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Support Type</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Key Activities</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><strong>ACE (Automated Commercial Environment)</strong></TableCell>
              <TableCell>Development & O&M</TableCell>
              <TableCell>Feature development, bug fixes, performance optimization</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>ATAP (Advanced Trade Analytics Program)</strong></TableCell>
              <TableCell>Analytics Expansion</TableCell>
              <TableCell>New analytics, dashboard development, data management</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Cargo Processing Systems</strong></TableCell>
              <TableCell>Maintenance & Enhancement</TableCell>
              <TableCell>Import/export processing, manifest management</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Trade Compliance Systems</strong></TableCell>
              <TableCell>Support & Development</TableCell>
              <TableCell>Enforcement tools, compliance tracking</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Integration Interfaces</strong></TableCell>
              <TableCell>API Development</TableCell>
              <TableCell>System-to-system interfaces, data exchange</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Contract Metrics */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <MoneyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Contract Value & Delivery Metrics
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #003366' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#003366', fontWeight: 'bold' }}>
                $43.8M
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Contract Value
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Bridge task order
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                Oct 2025
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Award Date
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Recent contract
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                2
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Major Platforms
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                ACE and ATAP
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                24/7
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Operations
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Continuous support
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Key Benefits */}
      <Card sx={{ mb: 4, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Mission Impact & Strategic Value
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Operational Continuity:</strong> Uninterrupted ACE and ATAP operations</Typography></li>
                <li><Typography variant="body2"><strong>Trade Protection:</strong> Maintains $2.5T+ annual trade processing</Typography></li>
                <li><Typography variant="body2"><strong>Development Momentum:</strong> Continues modernization progress</Typography></li>
                <li><Typography variant="body2"><strong>Risk Mitigation:</strong> Prevents service gaps and disruptions</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Agile Delivery:</strong> Rapid feature development and deployment</Typography></li>
                <li><Typography variant="body2"><strong>Security Integration:</strong> DevSecOps best practices</Typography></li>
                <li><Typography variant="body2"><strong>Legacy Modernization:</strong> Ongoing system improvements</Typography></li>
                <li><Typography variant="body2"><strong>Analytics Growth:</strong> Expanded analytical capabilities</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Relationship to Other Contracts */}
      <Card sx={{ mb: 4, bgcolor: '#fff3e0', border: '2px solid #F57C00' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#F57C00' }}>
            Contract Relationship & Portfolio
          </Typography>
          <Typography variant="body2" paragraph>
            This bridge contract complements Dev Technology Group's broader CBP contract portfolio:
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Cargo Systems O&M ($184M):
              </Typography>
              <Typography variant="body2">
                Long-term operations and maintenance contract for cargo systems
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                ACE & ATAP Enhancement ($136M):
              </Typography>
              <Typography variant="body2">
                Joint venture for modernization with emerging technologies
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Bridge Contract ($43.8M):
              </Typography>
              <Typography variant="body2">
                Interim support ensuring continuity during transition
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Reference Links */}
      <Card sx={{ mb: 4, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Contract References & Documentation
          </Typography>
          <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
            <li>
              <Link href="https://orangeslices.ai/dhs-cbp-awards-44m-ace-and-atap-platforms-application-development-and-om-support-bridge-contract/" target="_blank" rel="noopener">
                <Typography variant="body2">OrangeSlices.ai - $43.8M ACE and ATAP Bridge Contract Award</Typography>
              </Link>
            </li>
            <li>
              <Link href="https://www.usaspending.gov/recipient/d768b60a-beaa-2e46-7aa5-6df63e232bb2-C/latest" target="_blank" rel="noopener">
                <Typography variant="body2">USASpending.gov - Dev Technology Group Profile</Typography>
              </Link>
            </li>
            <li>
              <Link href="https://devtechnology.com/blog/" target="_blank" rel="noopener">
                <Typography variant="body2">Dev Technology Group - Company Blog</Typography>
              </Link>
            </li>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ACEATAPBridgeContractDetail;
